import React, { useMemo } from 'react';
import BaseTable from './BaseTable';
import { buildTable, customizeTable } from '../utils/tableBuilder';
import { generateDynamicColumns } from '../config/tableColumns';
import useDropdownData from '../fetches/loadDropDownData';
import { TableProvider } from '../hooks/useTableContext';

/**
 * Format data for the table from API response
 * @param {Array} recruits - Recruits data from API
 * @param {Object} formulasMapping - Mapping of formula data
 * @returns {Array} Formatted data for the table
 */
const formatTableData = (recruits, formulasMapping = {}) => {
  return recruits.map((recruit) => {
    const formulas = formulasMapping[recruit.id];
    const calculatePercentage = (value, divisor) => {
      if (!divisor) return 0;
      return Math.round((value / divisor) * 100) || 0;
    };
    
    return {
      rating: recruit.rating || 0,
      id: recruit.id,
      smgn: recruit.code,
      status: recruit.status,
      code: recruit.code,
      first_name: recruit.firstName,
      last_name: recruit.lastName,
      company: recruit.Company?.name || '',
      team: recruit.Team?.name || '',
      department: recruit.Department?.name || '',
      division: recruit.Division?.name || '',
      position: recruit.Position?.name || '',
      date_of_interview: recruit?.interviewAt || '',
      date_of_translate: recruit?.decryptionAt || '',
      commentary: recruit.comment,
      date_of_offer: recruit.employmentAt,
      date_of_fired: recruit.dismissalAt,
      working_phone: recruit.personalPhone,
      NDA_date: recruit.acceptNDA ? 'Yes' : 'No',
      FB_IG: recruit.fbMg,
      linkedin: recruit.linkedin,
      phone_number: recruit.personalPhone,

      // For each formula-based field, if formulas exist use them (multiplied by 100),
      // otherwise, calculate using recruit's own values.
      P01_0: calculatePercentage(recruit.P01_0, recruit.P01divisor),
      P01_1: calculatePercentage(recruit.P01_1, recruit.P01divisor),
      P01_2: calculatePercentage(recruit.P01_2, recruit.P01divisor),
      P01_3: calculatePercentage(recruit.P01_3, recruit.P01divisor),
      P01_4: calculatePercentage(recruit.P01_4, recruit.P01divisor),

      P02_0: calculatePercentage(recruit.P02_0, recruit.P02divisor),
      P02_1: calculatePercentage(recruit.P02_1, recruit.P02divisor),

      P03_0: calculatePercentage(recruit.P03_0, recruit.P03divisor),
      P03_1: calculatePercentage(recruit.P03_1, recruit.P03divisor),

      P04_0: calculatePercentage(recruit.P04_0, recruit.P04divisor),
      P04_1: calculatePercentage(recruit.P04_1, recruit.P04divisor),

      P05_0: calculatePercentage(recruit.P05_0, recruit.P05divisor),
      P05_1: calculatePercentage(recruit.P05_1, recruit.P05divisor),

      P06_0: calculatePercentage(recruit.P06_0, recruit.P06divisor),
      P06_1: calculatePercentage(recruit.P06_1, recruit.P06divisor),

      P07_0: calculatePercentage(recruit.P07_0, recruit.P07divisor),
      P07_1: calculatePercentage(recruit.P07_1, recruit.P07divisor),
      P07_2: calculatePercentage(recruit.P07_2, recruit.P07divisor),

      P08_0: calculatePercentage(recruit.P08_0, recruit.P08divisor),
      P08_1: calculatePercentage(recruit.P08_1, recruit.P08divisor),

      P09_0: calculatePercentage(recruit.P09_0, recruit.P09divisor),
      P09_1: calculatePercentage(recruit.P09_1, recruit.P09divisor),

      P10_0: calculatePercentage(recruit.P10_0, recruit.P10divisor),
      P10_1: calculatePercentage(recruit.P10_1, recruit.P10divisor),

      P11_0: calculatePercentage(recruit.P11_0, recruit.P11divisor),
      P11_1: calculatePercentage(recruit.P11_1, recruit.P11divisor),
      
      F1: formulas ? Math.round((formulas.F1 ?? 0) * 100) : 0,
      F2: formulas ? Math.round((formulas.F2 ?? 0) * 100) : 0,
      F3: formulas ? Math.round((formulas.F3 ?? 0) * 100) : 0,
      F4: formulas ? Math.round((formulas.F4 ?? 0) * 100) : 0,
      F5: formulas ? Math.round((formulas.F5 ?? 0) * 100) : 0,
      F6: formulas ? Math.round((formulas.F6 ?? 0) * 100) : 0,
      F7: formulas ? Math.round((formulas.F7 ?? 0) * 100) : 0,
      F8: formulas ? Math.round((formulas.F8 ?? 0) * 100) : 0,
      F9: formulas ? Math.round((formulas.F9 ?? 0) * 100) : 0,
      F10: formulas ? Math.round((formulas.F10 ?? 0) * 100) : 0,
      F11: formulas ? Math.round((formulas.F11 ?? 0) * 100) : 0,
      F12: formulas ? Math.round((formulas.F12 ?? 0) * 100) : 0,
    };
  });
};

/**
 * SummaryTable Component - A wrapper for BaseTable with specific configurations
 * @param {Object} props - Component props
 * @param {Object} props.filter - Filter state
 * @param {boolean} props.columnsVisible - Whether all columns are visible
 * @param {boolean} props.headersRotated - Whether headers are rotated
 * @param {boolean} props.dataEncrypted - Whether data is encrypted
 * @param {Object} props.columnVisibility - Column visibility state
 * @param {function} props.setColumnVisibility - Function to set column visibility
 * @param {Array} props.pinnedColumns - Pinned columns
 * @param {function} props.setPinnedColumns - Function to set pinned columns
 * @param {Array} props.sorting - Sorting configuration
 * @param {function} props.setSorting - Function to set sorting
 * @param {Object} props.filterOptions - Filter options
 * @param {Object} props.setterFunctions - Filter setter functions
 * @param {Array} props.columns - Optional array of column IDs to include
 */
const HrTable = ({ 
  filter,
  columnsVisible, 
  headersRotated = true, 
  dataEncrypted = false,
  columnVisibility,
  setColumnVisibility,
  pinnedColumns = [],
  setPinnedColumns,
  sorting,
  setSorting,
  filterOptions = {},
  setterFunctions = {},
  columns = [] // New prop: configurable columns
}) => {
  // Fetch dropdown data for filters
  const { dropdownData } = useDropdownData();

  // Default columns if none provided
  const defaultColumns = [
    'actions',
    'rating',
    'status',
    'code',
    'name',
    'company',
    'team',
    'department',
    'position',
    'output'
  ];

  // Detect dynamic columns from the column specification
  const dynamicColumns = useMemo(() => {
    // Get P and F columns
    const pColumns = [];
    const fColumns = [];
    
    columns.forEach(col => {
      if (col.startsWith('P')) {
        pColumns.push(col);
      } else if (col.startsWith('F')) {
        fColumns.push(col);
      }
    });
    
    return [...pColumns, ...fColumns];
  }, [columns]);

  // Build the table configuration
  const tableConfig = useMemo(() => {
    // If columns prop is provided, use it, otherwise use default columns
    const columnsToUse = columns.length > 0 ? columns : defaultColumns;
    
    // Add any dynamic P or F columns that might be missing
    Object.keys(generateDynamicColumns()).forEach(dynamicCol => {
      if (!columnsToUse.includes(dynamicCol) && dynamicColumns.includes(dynamicCol)) {
        columnsToUse.push(dynamicCol);
      }
    });
    
    // Build the table
    return buildTable(columnsToUse, {
      columnVisibility,
    });
  }, [columns, defaultColumns, dynamicColumns, columnVisibility]);

  // Memoize the data formatter to prevent unnecessary recreation
  const formatData = useMemo(() => {
    return (recruits, formulasMapping) => formatTableData(recruits, formulasMapping);
  }, []);

  // Initial column visibility based on provided columns
  const initialColumnVisibility = useMemo(() => {
    const visibility = {};
    const columnsToUse = columns.length > 0 ? columns : defaultColumns;
    
    // Set all specified columns to visible
    columnsToUse.forEach(col => {
      visibility[col] = true;
    });
    
    // Merge with any provided columnVisibility
    return { ...visibility, ...columnVisibility };
  }, [columns, defaultColumns, columnVisibility]);

  return (
    <TableProvider
      tableId="hr-table"
      initialVisibility={initialColumnVisibility}
      initialColumns={columns.length > 0 ? columns : defaultColumns}
    >
      <BaseTable
        tableConfig={tableConfig}
        formatData={formatData}
        tableId="hr-table"
        columnOrder={columns.length > 0 ? columns : defaultColumns}
        dropdownData={dropdownData}
      />
    </TableProvider>
  );
};

export default HrTable;